/* ============================================================
   SHINE & STYLE — premium motion
   Scroll progress, line-masked headings, parallax, magnetic
   buttons, card tilt, gallery, accordions, page transitions.

   Every effect here is an enhancement. If this file fails to
   load the pages still render and every control still works.
   ============================================================ */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* One shared rAF loop for everything scroll-driven. Separate
     listeners per effect would each force their own layout. */
  var scrollJobs = [];
  var scrolling = false;

  function onScrollFrame() {
    for (var i = 0; i < scrollJobs.length; i++) scrollJobs[i]();
    scrolling = false;
  }
  window.addEventListener('scroll', function () {
    if (!scrolling) { scrolling = true; window.requestAnimationFrame(onScrollFrame); }
  }, { passive: true });
  window.addEventListener('resize', onScrollFrame, { passive: true });

  /* ---------------------------------------------------------
     Scroll progress
     --------------------------------------------------------- */
  var progress = document.querySelector('[data-progress]');
  if (progress) {
    scrollJobs.push(function () {
      var max = document.documentElement.scrollHeight - window.innerHeight;
      var p = max > 0 ? window.scrollY / max : 0;
      progress.style.setProperty('--p', Math.min(1, Math.max(0, p)).toFixed(4));
    });
  }

  /* ---------------------------------------------------------
     Line-masked headings
     Wrap each rendered line in its own clipping box. Uses Range
     rectangles to find real line breaks, so it survives any
     font size or column width rather than guessing at <br>.
     --------------------------------------------------------- */
  function maskLines(el) {
    if (el.dataset.masked === '1') return;

    /* Always rebuild from the text captured on the very first pass.
       Reading el.textContent again would concatenate the previous
       pass's per-line spans, whose trailing spaces have been
       trimmed away — so "for every" comes back as "forevery" and
       gets a little more corrupted on every reflow. */
    if (el.dataset.text == null) {
      el.dataset.text = el.textContent.replace(/\s+/g, ' ').trim();
    }
    var text = el.dataset.text;
    if (!text) return;

    // Rebuild as words so each can be measured.
    el.textContent = '';
    var words = text.split(' ').map(function (w, i) {
      var s = document.createElement('span');
      s.textContent = (i ? ' ' : '') + w;
      el.appendChild(s);
      return s;
    });

    // Group words by their vertical offset — that is a line.
    var lines = [];
    var currentTop = null;
    words.forEach(function (w) {
      var top = Math.round(w.getBoundingClientRect().top);
      if (currentTop === null || Math.abs(top - currentTop) > 3) {
        lines.push([]); currentTop = top;
      }
      lines[lines.length - 1].push(w);
    });

    el.textContent = '';
    lines.forEach(function (line, i) {
      var mask = document.createElement('span');
      mask.className = 'ss-mask';
      var inner = document.createElement('span');
      inner.style.setProperty('--line', String(i));
      inner.textContent = line.map(function (w) { return w.textContent; }).join('').trim();
      mask.appendChild(inner);
      el.appendChild(mask);
    });

    el.dataset.masked = '1';
  }

  var headings = document.querySelectorAll('[data-mask-lines]');

  function remask() {
    Array.prototype.forEach.call(headings, function (h) {
      var wasIn = h.classList.contains('is-in');
      h.dataset.masked = '';
      h.textContent = h.dataset.text || h.textContent;
      maskLines(h);
      if (wasIn) h.classList.add('is-in');
    });
  }

  if (headings.length && !reduced) {
    Array.prototype.forEach.call(headings, maskLines);

    /* The first pass runs in whatever font is available. A webfont
       swapping in afterwards changes where the lines break, which
       would leave words masked into the wrong boxes — so measure
       again once the fonts have actually loaded. */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(remask);
    }

    // Re-wrap when the line count can change.
    var reflowTimer;
    window.addEventListener('resize', function () {
      window.clearTimeout(reflowTimer);
      reflowTimer = window.setTimeout(remask, 220);
    });
  }

  /* ---------------------------------------------------------
     Reveal — headings, images and anything else opted in.
     ss-motion.js handles [data-reveal]; this covers the two
     premium variants, with the same failsafe.
     --------------------------------------------------------- */
  var premiumTargets = document.querySelectorAll('[data-mask-lines], [data-img-reveal]');

  if (reduced || !('IntersectionObserver' in window)) {
    Array.prototype.forEach.call(premiumTargets, function (el) { el.classList.add('is-in'); });
  } else if (premiumTargets.length) {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.1 });

    Array.prototype.forEach.call(premiumTargets, function (el) { io.observe(el); });

    // Same reasoning as ss-motion.js: a start state of hidden must
    // never be able to become permanent.
    window.setTimeout(function () {
      Array.prototype.forEach.call(
        document.querySelectorAll('[data-mask-lines]:not(.is-in), [data-img-reveal]:not(.is-in)'),
        function (el) { el.classList.add('is-in'); io.unobserve(el); }
      );
    }, 2500);
  }

  /* ---------------------------------------------------------
     Parallax
     --------------------------------------------------------- */
  var parallax = Array.prototype.slice.call(document.querySelectorAll('[data-parallax]'));
  if (parallax.length && !reduced) {
    scrollJobs.push(function () {
      var vh = window.innerHeight;
      parallax.forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) return;            // off-screen, skip
        var speed = parseFloat(el.getAttribute('data-parallax')) || 0.12;
        // -1 at the top of the viewport, +1 at the bottom.
        var mid = (r.top + r.height / 2 - vh / 2) / (vh / 2);
        el.style.setProperty('--y', (mid * speed * -100).toFixed(1) + 'px');
      });
    });
    onScrollFrame();
  }

  /* ---------------------------------------------------------
     Magnetic buttons — pointer devices only. On touch the
     element would just drift under the finger.
     --------------------------------------------------------- */
  if (finePointer && !reduced) {
    Array.prototype.forEach.call(document.querySelectorAll('[data-magnetic]'), function (el) {
      var strength = parseFloat(el.getAttribute('data-magnetic')) || 0.28;

      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        el.classList.add('is-tracking');
        el.style.setProperty('--mx', ((e.clientX - (r.left + r.width / 2)) * strength).toFixed(1) + 'px');
        el.style.setProperty('--my', ((e.clientY - (r.top + r.height / 2)) * strength).toFixed(1) + 'px');
      });

      el.addEventListener('pointerleave', function () {
        el.classList.remove('is-tracking');
        el.style.setProperty('--mx', '0px');
        el.style.setProperty('--my', '0px');
      });
    });
  }

  /* ---------------------------------------------------------
     Card tilt
     --------------------------------------------------------- */
  if (finePointer && !reduced) {
    Array.prototype.forEach.call(document.querySelectorAll('[data-tilt]'), function (card) {
      var media = card.querySelector('.ss-card__media');
      if (!media) return;

      card.addEventListener('pointermove', function (e) {
        var r = media.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width  - 0.5;
        var py = (e.clientY - r.top)  / r.height - 0.5;
        media.style.setProperty('--ry', (px * 6).toFixed(2) + 'deg');
        media.style.setProperty('--rx', (-py * 6).toFixed(2) + 'deg');
      });

      card.addEventListener('pointerleave', function () {
        media.style.setProperty('--ry', '0deg');
        media.style.setProperty('--rx', '0deg');
      });
    });
  }

  /* ---------------------------------------------------------
     Product gallery
     --------------------------------------------------------- */
  var stage = document.querySelector('[data-gallery-stage]');
  if (stage) {
    var slides = stage.querySelectorAll('img');
    var thumbs = document.querySelectorAll('[data-gallery-thumb]');

    var show = function (index) {
      Array.prototype.forEach.call(slides, function (img, i) {
        img.classList.toggle('is-active', i === index);
      });
      Array.prototype.forEach.call(thumbs, function (t, i) {
        t.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    };

    Array.prototype.forEach.call(thumbs, function (t, i) {
      t.addEventListener('click', function () { show(i); });
    });

    show(0);
  }

  /* ---------------------------------------------------------
     Option pickers — sizes, colours, filter pills.
     Single-select within a group; the group is the parent.
     --------------------------------------------------------- */
  Array.prototype.forEach.call(document.querySelectorAll('[data-pick-group]'), function (group) {
    var buttons = group.querySelectorAll('[data-pick]');
    Array.prototype.forEach.call(buttons, function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        Array.prototype.forEach.call(buttons, function (b) { b.setAttribute('aria-pressed', 'false'); });
        btn.setAttribute('aria-pressed', 'true');

        var out = group.getAttribute('data-pick-group');
        var target = out ? document.querySelector('[data-pick-output="' + out + '"]') : null;
        if (target) target.textContent = btn.getAttribute('data-pick') || btn.textContent.trim();
      });
    });
  });

  /* ---------------------------------------------------------
     Product accordions
     --------------------------------------------------------- */
  Array.prototype.forEach.call(document.querySelectorAll('[data-acc]'), function (btn) {
    btn.addEventListener('click', function () {
      var open = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!open));
      var panel = document.getElementById(btn.getAttribute('data-acc'));
      if (panel) panel.classList.toggle('is-open', !open);
    });
  });

  /* ---------------------------------------------------------
     Sticky buy bar — appears once the real Add to cart has
     scrolled past, so there is never a duplicate button on screen.
     --------------------------------------------------------- */
  var buybar = document.querySelector('[data-buybar]');
  var buyAnchor = document.querySelector('[data-buy-anchor]');
  if (buybar && buyAnchor) {
    scrollJobs.push(function () {
      buybar.classList.toggle('is-visible', buyAnchor.getBoundingClientRect().bottom < 0);
    });
    onScrollFrame();
  }

  /* ---------------------------------------------------------
     Page transition
     Wipes a curtain up, then navigates. Skips anything that is
     not a plain same-document navigation — new tabs, modified
     clicks, downloads, hashes and other origins all pass through
     untouched, because swallowing those would break the browser.
     --------------------------------------------------------- */
  var curtain = document.querySelector('[data-curtain]');
  if (curtain && !reduced) {
    // Play the incoming half on arrival.
    curtain.classList.add('is-out');
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        curtain.classList.remove('is-out');
        curtain.classList.add('is-in');
      });
    });

    document.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest('a[href]');
      if (!link) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      if (link.target && link.target !== '_self') return;
      if (link.hasAttribute('download')) return;

      var href = link.getAttribute('href');
      if (!href || href.charAt(0) === '#') return;

      var url;
      try { url = new URL(link.href); } catch (err) { return; }
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.hash) return;

      e.preventDefault();
      curtain.classList.remove('is-in');
      curtain.classList.add('is-out');
      window.setTimeout(function () { window.location.href = link.href; }, 420);
    });

    // Coming back via the bfcache would otherwise leave the
    // curtain down over the restored page.
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      curtain.classList.remove('is-out');
      curtain.classList.add('is-in');
    });
  }
})();
